import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DeleteIcon } from '@assets/icons/delete.svg';
import { ReactComponent as PinnedIcon } from '@assets/icons/pinned.svg';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError } from '@components/Toast';

import CreateNewProduct from '@features/collection/CreateNewProduct';
import DesignCard from '@features/collection/DesignCard';

import { getCollections, getMarkets } from '@store/design/action';
import { getAllCollections, getAllMarkets } from '@store/reducers';

import ApiClient from '@lib/apiClient';

import { addImageSuffix, getUserInfo, onErrorImageLoad, printApiError } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const UploadCollectionImage = ({
	collectionId,
	addNewDesign,
	productIdsOnCollection,
	addToCollection,
	isUpdating
}) => {
	const userType = getUserInfo().primaryUserType;
	const [loader, setLoader] = useState(false);
	const [imageList, setImageList] = useState({});
	const [selectedImage, setSelectedImage] = useState([]);
	const [categories, setCategories] = useState([]);
	const [openCreateModal, setOpenCreateModal] = useState(false);
	const [active, setActive] = useState(userType === 'FASHION_DESIGNER' ? 'new' : 'collection');
	const [searchCollection, setSearchCollection] = useState('');
	const [selectedCollectionName, setSelectedCollectionName] = useState('');
	const [designNumber, setDesignNumber] = useState('');
	const [selectedMarket, setSelectedMarket] = useState(null);
	const [selectedCategories, setSelectedCategories] = useState(null);
	const [productList, setProductList] = useState(null);
	const [totalElements, setTotalElements] = useState(null);
	const [selectedCollectionId, setSelectedCollectionId] = useState(null);
	const myRef = useRef(null);
	const dispatch = useDispatch();

	let markets = useSelector(getAllMarkets);
	markets = markets.map((item) => {
		return {
			id: item.id,
			value: item.name
		};
	});

	let collections = useSelector(getAllCollections);
	collections = collections.map((item) => {
		return {
			id: item.id,
			value: item.collectionName
		};
	});

	useEffect(() => {
		dispatch(getCollections(searchCollection));
	}, [dispatch, searchCollection]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		if (myRef && !myRef.current.contains(event.target)) {
			setSelectedImage([]);
			setOpenCreateModal(false);
		}
	};

	const getCollectionImages = async () => {
		setLoader(true);
		await ApiClient.GET('collectionProductDocument', `${collectionId}/product-document`)
			.then((response) => {
				setImageList(response.data);
				setLoader(false);
			})
			.catch((err) => {
				setLoader(false);
				toastError(err.response.data.message);
			});
	};

	useEffect(() => {
		dispatch(getMarkets());
		fetchSubCategories();
		getCollectionImages();
	}, [collectionId]);

	const fetchSubCategories = (productGroupId) => {
		ApiClient.GET('fetchSubCategory', `${productGroupId ? '?productGroupIds=' + productGroupId : ''}`)
			.then((response) => {
				let tmpCategories = [];
				for (let cat of response.data) {
					for (let item of cat.subCategoryResponseList) {
						tmpCategories.push({ id: item.id, value: item.name, isPrimary: item.isPrimary });
					}
				}
				setCategories(tmpCategories);
				setLoader(false);
			})
			.catch((error) => {
				setLoader(false);
				toastError(error.response.data.message);
			});
	};

	const selectImage = (documentId, type) => {
		let selectedImageByUser = imageList[type]?.find((item) => item.id === documentId);
		let checkExistItem = selectedImage.find((item) => item.id === documentId);
		if (checkExistItem == undefined) {
			setSelectedImage([...selectedImage, selectedImageByUser]);
		} else {
			let newSelectedItem = selectedImage?.filter((item) => item.id !== documentId);
			setSelectedImage(newSelectedItem);
		}
		setOpenCreateModal(true);
	};

	const removeFromSelectedImage = (documentId) => {
		let selectedImageList = selectedImage.filter((item, index) => index !== documentId);
		setSelectedImage(selectedImageList);
	};

	const pinOrUnpinImage = async (documentId, type) => {
		setLoader(true);
		await ApiClient.PUT(
			`${BASE_URL}/collection/ ${collectionId}/product-document/${documentId}/${type}`,
			'',
			'',
			true
		)
			.then(() => {
				getCollectionImages();
			})
			.catch((err) => {
				setLoader(false);
				toastError(err.response.data.message);
			});
	};

	const deleteImage = async (documentId) => {
		setLoader(true);
		await ApiClient.DELETE(
			`${BASE_URL}/collection/ ${collectionId}/product-document/${documentId}`,
			'',
			'',
			true
		)
			.then(() => {
				getCollectionImages();
			})
			.catch((err) => {
				setLoader(false);
				toastError(err.response.data.message);
			});
	};

	const uploadBulkImages = async (e, docType = 'REFERENCE_IMAGE') => {
		setLoader(true);
		let allFileList = [];
		let files = [];
		files = [...e.target.files];
		files.length !== 0 &&
			(await files.forEach((file) => {
				if (file) {
					let data = {
						name: file?.name,
						docMimeType: file?.type,
						documentType: docType,
						base64Str: ''
					};
					let reader = new FileReader();
					reader.readAsDataURL(file);
					reader.onload = () => {
						data.base64Str = reader.result;
						allFileList.push(data);
					};
					reader.onerror = function (error) {
						toastError(error);
					};
				}
			}));
		setTimeout(() => {
			ApiClient.POST(`${BASE_URL}/collection/${collectionId}/product-document`, allFileList, '', true)
				.then(() => {
					getCollectionImages();
				})
				.catch((err) => {
					setLoader(false);
					toastError(err.response.data.message);
				});
		}, 100);
	};

	const closeCreateModal = () => {
		setSelectedImage([]);
		setOpenCreateModal(false);
		// myRef.current.classList.remove("show")
		// document.querySelector(".modal-backdrop").setAttribute("style", "background-color: transparent")
	};

	const renderImages = (type) => {
		return imageList[type]?.map((item, index) => {
			return (
				<div className="mItem" key={`unused_${index}`}>
					<DeleteIcon className="delete" onClick={() => deleteImage(item.id)} />
					{type === 'pinnedList' ? (
						<PinnedIcon
							className="pin pinned"
							onClick={() => pinOrUnpinImage(item.id, 'unpinned')}
						/>
					) : (
						<CommonImage
							src={`/icons/Pin.png`}
							alt="pin"
							className="pin"
							onClick={() => pinOrUnpinImage(item.id, 'pinned')}
						/>
					)}
					<CommonImage
						src={addImageSuffix(item.docUrl, '')}
						onError={(e) => onErrorImageLoad(e, item.docUrl)}
						onClick={() => selectImage(item.id, type)}
						className={
							selectedImage.map((image) => image.id).includes(item.id)
								? 'design-image cursor-pointer selected'
								: 'design-image cursor-pointer'
						}
						isStatic={false}
					/>
				</div>
			);
		});
	};

	const getCollectionProducts = async (type = 'byCollection', collectionId, page = 0, merge = false) => {
		setLoader(true);
		let params;
		let url;
		let requestBody = {};
		if (type === 'byCollection') {
			url = 'getCollectionProducts';
		} else {
			url = 'getManageProducts';
			params = `?page=${page}&size=${20}&sort=id,asc`;
			if (collectionId !== null) {
				requestBody.collectionId = collectionId;
			}
			if (designNumber !== '') {
				requestBody.search = designNumber;
			}
			if (selectedMarket !== null) {
				requestBody.marketIdList = [selectedMarket?.id];
			}
			if (selectedCategories !== null) {
				requestBody.subCategoryIdList = [selectedCategories?.id];
			}
			if (selectedCategories !== null || selectedMarket !== null) {
				requestBody.availabilityStatusList = ['PUBLISHED'];
			}
		}

		try {
			const { data } = await ApiClient.POST(url, requestBody, params);

			setLoader(false);
			if (data) {
				if (merge) {
					setProductList({ ...data, data: [...productList.data, ...data.data] });
				} else {
					setProductList(data);
				}
				setTotalElements(data.totalElements);
			}
		} catch (error) {
			setLoader(false);
			printApiError(error);
		}
	};

	const onScrollList = (event) => {
		const { scrollHeight, scrollTop, clientHeight } = event.target;
		const scroll = scrollHeight - scrollTop - clientHeight;
		let hasNext = productList?.currentPage + 1 < productList?.totalPages;
		const threshold = 1;

		if (scroll <= threshold && hasNext) {
			let apiType = selectedCollectionId !== null ? 'byCollection' : 'byMarketsOrCategories';
			getCollectionProducts(apiType, selectedCollectionId, productList?.currentPage + 1, true);
		}
	};

	const onSelectCollection = (collectionInfo) => {
		setSelectedCategories(null);
		setSelectedMarket(null);
		setDesignNumber('');
		setSelectedCollectionId(collectionInfo.id);
		setSelectedCollectionName(collectionInfo.value);
		getCollectionProducts('byCollection', collectionInfo.id);
	};

	useEffect(() => {
		if (selectedMarket !== null || selectedCategories !== null || designNumber !== '') {
			getCollectionProducts('byMarketsOrCategories');
		}
	}, [selectedMarket, selectedCategories, designNumber]);

	const onSelectMarket = (marketInfo) => {
		setSelectedMarket(marketInfo);
		fetchSubCategories(marketInfo.id);
		setSelectedCollectionId(null);
		setSelectedCollectionName('');
	};

	const onSelectCategories = (categoryInfo) => {
		setSelectedCategories(categoryInfo);
		setSelectedCollectionId(null);
		setSelectedCollectionName('');
	};

	const handleDesignNumber = (e) => {
		setDesignNumber(e.target.value);
		setSelectedCollectionId(null);
		setSelectedCollectionName('');
	};

	const renderDesignList = () => {
		return (
			<LoaderComponent loading={isUpdating}>
				<DesignCard
					productList={productList?.data ?? []}
					onScrollList={(e) => onScrollList(e)}
					productIdsOnCollection={productIdsOnCollection}
					addToCollection={(product) => addToCollection(product)}
				/>
			</LoaderComponent>
		);
	};

	const renderDesignCount = () => {
		return (
			<div className="d-flex justify-content-between px-2 mx-1 mb-2">
				{totalElements !== null && (
					<p className="regular-14 gray_dark_02">
						{totalElements} style{' '}
						{`${
							selectedMarket !== null || selectedCategories !== null ? 'from nitex studio' : ''
						}`}
					</p>
				)}

				{(selectedCollectionId !== null ||
					selectedMarket !== null ||
					selectedCategories !== null ||
					designNumber !== '') && (
					<button className="button text-only" onClick={clearAll}>
						Clear
					</button>
				)}
			</div>
		);
	};

	const clearAll = () => {
		setSelectedCollectionId(null);
		setSelectedCollectionName('');
		setSelectedCategories(null);
		setSelectedMarket(null);
		setDesignNumber('');
		setProductList([]);
		setTotalElements(null);
	};

	return (
		<LoaderComponent loading={loader}>
			<div
				ref={myRef}
				className="modal modal-right fade add-multiple-design"
				id="add-multiple-design"
				tabIndex={-1}
				role="dialog"
				aria-labelledby="right_modal"
			>
				<div className="modal-dialog" role="document">
					<div className="modal-content">
						<div
							className="close-icon d-flex justify-content-end cursor-pointer text-right w-100 pt-3 pr-3"
							data-dismiss="modal"
							aria-label="Close"
						>
							<CommonImage
								src="/icons/close-icon.svg"
								alt="close"
								width={16}
								height={16}
								className="close-icon-image"
								onClick={closeCreateModal}
							/>
						</div>
						<nav id="uploaded-nav" className="navbar">
							{userType === 'FASHION_DESIGNER' && (
								<ul className="nav nav-pills">
									<li className="nav-item">
										<a
											className={active === 'new' ? 'nav-link active' : 'nav-link'}
											href="#new-tab"
											onClick={() => setActive('new')}
										>
											NEW
										</a>
									</li>
									<li className="nav-item">
										<a
											className={active === 'used' ? 'nav-link active' : 'nav-link'}
											href="#used-tab"
											onClick={() => setActive('used')}
										>
											USED
										</a>
									</li>
									<li className="nav-item">
										<a
											className={active === 'pinned' ? 'nav-link active' : 'nav-link'}
											href="#pinned-tab"
											onClick={() => setActive('pinned')}
										>
											PINNED
										</a>
									</li>
								</ul>
							)}

							<div className="from-collection-tab">
								<p
									className={
										active === 'collection'
											? 'nav-link px-0 pb-1 active'
											: 'nav-link px-0'
									}
									onClick={() => setActive('collection')}
								>
									<CommonImage src={`/icons/Add-design.svg`} alt="add" className="mr-1" />
									ADD Design
								</p>
							</div>
						</nav>
						{active === 'collection' ? (
							<div>
								<div className="select-from-collection">
									<div className="row">
										<div className="col-md-6 pr-1 mb-2">
											<CustomDropdown
												type="addItem"
												title={'Collection'}
												isFocus={true}
												onItemClick={(value) => {
													onSelectCollection(value);
												}}
												toolTipLength={25}
												onItemSearch={(value) => setSearchCollection(value)}
												items={collections}
												selectedItem={selectedCollectionName}
											/>
										</div>
										<div className="col-md-6  pr-1 mb-2">
											<div className="with-icon right-icon">
												<input
													type="text"
													autoComplete="off"
													placeholder="Design number"
													onChange={handleDesignNumber}
													value={designNumber}
												/>
												<span className="right-icon">
													<svg
														width={17}
														height={17}
														viewBox="0 0 17 17"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M7.5 0C11.6421 0 15 3.35786 15 7.5C15 9.21015 14.4276 10.7866 13.464 12.0483L16.7071 15.2929C17.0976 15.6834 17.0976 16.3166 16.7071 16.7071C16.3466 17.0676 15.7794 17.0953 15.3871 16.7903L15.2929 16.7071L12.0483 13.464C10.7866 14.4276 9.21015 15 7.5 15C3.35786 15 0 11.6421 0 7.5C0 3.35786 3.35786 0 7.5 0ZM7.5 2C4.46243 2 2 4.46243 2 7.5C2 10.5376 4.46243 13 7.5 13C8.85114 13 10.0885 12.5128 11.0459 11.7045C11.091 11.5536 11.1738 11.412 11.2929 11.2929C11.412 11.1738 11.5536 11.091 11.7041 11.0446C12.5128 10.0885 13 8.85114 13 7.5C13 4.46243 10.5376 2 7.5 2Z"
															fill="#9AA1AF"
														/>
													</svg>
												</span>
											</div>
										</div>
										<div className="col-md-6 pr-1 mb-2">
											<CustomDropdown
												type="addItem"
												title={'Market'}
												isFocus={true}
												onItemClick={(value) => {
													onSelectMarket(value);
												}}
												toolTipLength={25}
												items={markets}
												selectedItem={selectedMarket?.value}
											/>
										</div>
										<div className="col-md-6 pr-1 mb-2">
											<CustomDropdown
												type="addItem"
												title={'Categories'}
												isFocus={true}
												onItemClick={(value) => {
													onSelectCategories(value);
												}}
												toolTipLength={18}
												items={categories}
												selectedItem={selectedCategories?.value}
											/>
										</div>
									</div>
								</div>
								{renderDesignCount()}
								{renderDesignList()}
							</div>
						) : (
							<>
								<div className="style-upload text-center">
									<label htmlFor="style-upload" className="drag-upload">
										<CommonImage src={`/icons/upload-sm.svg`} alt="Upload po" />
										<span>UPLOAD</span>
									</label>
									<input
										type="file"
										className="file-upload d-none"
										name="style-upload"
										id="style-upload"
										multiple={true}
										accept="image/*"
										onChange={uploadBulkImages}
									/>
								</div>
								<div
									className="scroll-parent scroll-y-label"
									data-spy="scroll"
									data-target="#uploaded-nav"
									data-offset={104}
								>
									<div className="tab all-tab" id="new-tab">
										<div className="tab-heading">
											<h3>New</h3>
										</div>
										<div className="masonry-images">{renderImages('unUsedList')}</div>
									</div>
									{imageList['usedList']?.length > 0 && (
										<div className="tab used-tab" id="used-tab">
											<div className="tab-heading">
												<h3>USED</h3>
											</div>
											<div className="masonry-images">{renderImages('usedList')}</div>
										</div>
									)}
									{imageList['pinnedList']?.length > 0 && (
										<div className="tab pinned-tab" id="pinned-tab">
											<div className="tab-heading">
												<h3>PINNED</h3>
											</div>
											<div className="masonry-images">{renderImages('pinnedList')}</div>
										</div>
									)}
								</div>
								{openCreateModal && (
									<CreateNewProduct
										imageList={selectedImage}
										collectionId={collectionId}
										closeModal={closeCreateModal}
										removeImage={removeFromSelectedImage}
										addNewDesign={addNewDesign}
										setLoader={setLoader}
										getCollectionImages={getCollectionImages}
									/>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default UploadCollectionImage;
